import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { useCopy } from "./CopyContext.js";
function paragraphsOf(text) {
    return text
        .split(/\n\s*\n/)
        .map((p) => p.trim())
        .filter(Boolean)
        .map((p, i) => _jsx("p", { children: p }, i));
}
function lines(text) {
    const parts = text.split("\n");
    if (parts.length === 1)
        return text;
    return parts.map((line, i) => (_jsxs("span", { children: [i > 0 ? _jsx("br", {}) : null, line] }, i)));
}
/**
 * Permission-gated inline text. Visitors get exactly the plain element (no wrapper, no extra
 * nodes). Staff get the same element with an edit affordance: click, Enter or F2 opens the
 * editor; blur or Enter saves (Ctrl+Enter for multiline); Escape cancels. Save status and errors
 * are announced. Blank saves restore the configured copy.
 */
export function EditableText({ copyKey, fallback, label, as: Tag = "p", className, multiline: multilineProp = false, paragraphs = false, attrs, bindField, }) {
    const multiline = multilineProp || paragraphs;
    const render = (value) => paragraphs ? paragraphsOf(value) : multiline ? lines(value) : value;
    const api = useCopy();
    const text = bindField ? fallback : api.resolve(copyKey, fallback);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(text);
    const [attempt, setAttempt] = useState(null);
    const [fieldError, setFieldError] = useState(null);
    const [fieldBusy, setFieldBusy] = useState(false);
    useEffect(() => {
        if (!editing)
            setDraft(text);
    }, [text, editing]);
    if (!api.canEdit) {
        if (!text.trim())
            return null;
        return (_jsx(Tag, { className: className, ...attrs, children: render(text) }));
    }
    const busy = bindField ? fieldBusy : api.saving === copyKey;
    const error = bindField ? fieldError : (api.errors[copyKey] ?? null);
    const save = async (next) => {
        setEditing(false);
        if (next.trim() === text.trim())
            return;
        setAttempt(next);
        if (bindField) {
            if (!api.saveField)
                return;
            setFieldBusy(true);
            setFieldError(null);
            try {
                await api.saveField(bindField, next.trim());
                setAttempt(null);
            }
            catch (e) {
                setFieldError(e instanceof Error ? e.message : "Could not save.");
            }
            finally {
                setFieldBusy(false);
            }
            return;
        }
        if (await api.commit(copyKey, next))
            setAttempt(null);
    };
    const common = {
        className: "lumora-editable lumora-copy-input",
        "aria-label": label,
        "data-lumora-editable": "true",
        "data-lumora-copy-editing": copyKey,
        value: draft,
        placeholder: label,
        autoFocus: true,
        onChange: (e) => setDraft(e.target.value),
        onBlur: () => void save(draft),
    };
    if (editing) {
        const cancelOnEscape = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                setDraft(text);
                setEditing(false);
            }
        };
        return multiline ? (_jsx("textarea", { ...common, rows: 4, onKeyDown: (e) => {
                cancelOnEscape(e);
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey))
                    e.target.blur();
            } })) : (_jsx("input", { ...common, type: "text", onKeyDown: (e) => {
                cancelOnEscape(e);
                if (e.key === "Enter")
                    e.target.blur();
            } }));
    }
    const empty = !text.trim();
    const open = () => setEditing(true);
    return (_jsxs(_Fragment, { children: [_jsx(Tag, { className: [className, "lumora-copy"].filter(Boolean).join(" "), tabIndex: 0, title: `Edit: ${label}`, "data-lumora-copy": copyKey, ...(empty ? { "data-lumora-copy-empty": "true" } : {}), onClick: open, onKeyDown: (e) => {
                    if (e.key === "Enter" || e.key === "F2") {
                        e.preventDefault();
                        open();
                    }
                }, ...attrs, children: empty ? `Add ${label.toLowerCase()}` : render(text) }), busy ? (_jsx("span", { className: "lumora-copy-status", role: "status", "data-lumora-chrome": true, children: "Saving." })) : null, error ? (_jsxs("span", { className: "lumora-copy-status lumora-copy-status--error", role: "alert", "data-lumora-chrome": true, children: [error, " ", attempt != null ? (_jsx("button", { type: "button", className: "lumora-copy-retry", onClick: () => void save(attempt), children: "Retry" })) : null] })) : null] }));
}
//# sourceMappingURL=EditableText.js.map